/**
 * Formatting context - provides the current organisation's locale and
 * currency to any shared component (e.g. `DatePicker`, `formatDisplayValue`)
 * without coupling those components to the admin- or consumer-specific org
 * hooks.
 *
 * Wire-up:
 *   // admin/App.tsx
 *   const { preferences } = useOrg()
 *   <FormattingProvider
 *     locale={countryToLocale(preferences?.default_country_code, preferences?.default_language)}
 *     currency={preferences?.default_currency ?? 'GBP'}
 *     availableCurrencies={preferences?.available_currencies}
 *   >
 *     ...
 *   </FormattingProvider>
 *
 *   // shared component
 *   const { locale, currency } = useFormattingContext()
 */

import { createContext, useMemo, type ReactNode } from 'react'
import { DEFAULT_COUNTRY_CODE, DEFAULT_CURRENCY, DEFAULT_LOCALE } from './locale'

export interface FormattingContextValue {
  /** BCP 47 locale tag (e.g. 'en-GB'). Always defined. */
  locale: string
  /** ISO 4217 currency code (e.g. 'GBP'). Always defined. */
  currency: string
  /** ISO 3166-1 alpha-2 country code (e.g. 'GB'). Always defined. */
  defaultCountryCode: string
  /**
   * The currencies this organisation has switched on
   * (``OrganisationPreferences.available_currencies``), headed by
   * ``currency``. This is the ONLY list a currency picker may offer - the
   * money widget must never show a currency the org has not selected.
   * Always non-empty (falls back to ``[currency]``).
   */
  availableCurrencies: string[]
}

/**
 * Normalise a stored currency list into the option set a picker may show:
 * upper-cased, trimmed, de-duplicated, headed by ``currency`` so the org's
 * default is always offered and always first.
 *
 * This is the trust boundary for the whole currency path. ``available`` arrives
 * from ``OrganisationPreferences.available_currencies``, a **JSONField** - a
 * malformed row can hold a string or an object, and throwing here would take
 * down every consumer of the provider, not just a currency picker. So the shape
 * is checked rather than assumed, and everything downstream may rely on the
 * non-empty ``string[]`` this returns.
 */
function resolveAvailableCurrencies(
  currency: string,
  available: readonly string[] | null | undefined,
): string[] {
  const codes = (Array.isArray(available) ? available : [])
    .map((code) => (typeof code === 'string' ? code.trim().toUpperCase() : ''))
    .filter((code) => code.length > 0)
  return Array.from(new Set([currency, ...codes]))
}

const DEFAULT_CONTEXT: FormattingContextValue = {
  locale: DEFAULT_LOCALE,
  currency: DEFAULT_CURRENCY,
  defaultCountryCode: DEFAULT_COUNTRY_CODE,
  availableCurrencies: [DEFAULT_CURRENCY],
}

// Exported so `useFormattingContext` (in its own file, to keep
// react-refresh happy) can read it.
// eslint-disable-next-line react-refresh/only-export-components
export const FormattingContext = createContext<FormattingContextValue>(DEFAULT_CONTEXT)

interface FormattingProviderProps {
  /** BCP 47 locale tag. Falls back to the default if undefined/empty. */
  locale?: string | null
  /** ISO 4217 currency code. Falls back to the default if undefined/empty. */
  currency?: string | null
  /** ISO 3166-1 alpha-2 country code. Falls back to the default if undefined/empty. */
  defaultCountryCode?: string | null
  /**
   * The org's selected currencies (``available_currencies``). Falls back to
   * just the resolved default currency, so a picker never offers a currency
   * the organisation has not switched on.
   */
  availableCurrencies?: readonly string[] | null
  children: ReactNode
}

export function FormattingProvider({
  locale,
  currency,
  defaultCountryCode,
  availableCurrencies,
  children,
}: FormattingProviderProps) {
  const value = useMemo<FormattingContextValue>(() => {
    const resolvedCurrency = currency && currency.length > 0 ? currency : DEFAULT_CURRENCY
    return {
      locale: locale && locale.length > 0 ? locale : DEFAULT_LOCALE,
      currency: resolvedCurrency,
      defaultCountryCode:
        defaultCountryCode && defaultCountryCode.length > 0
          ? defaultCountryCode
          : DEFAULT_COUNTRY_CODE,
      availableCurrencies: resolveAvailableCurrencies(resolvedCurrency, availableCurrencies),
    }
  }, [locale, currency, defaultCountryCode, availableCurrencies])

  return <FormattingContext.Provider value={value}>{children}</FormattingContext.Provider>
}
